import { Injectable } from '@angular/core';
import {BehaviorSubject, firstValueFrom, Observable} from "rxjs";
import {INotification} from "../notification/notification.component";
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { IPagedResults } from 'src/app/core/models/base';


@Injectable({
  providedIn: 'root'
})
export class AttachmentService {

    constructor(private http: HttpClient) { }

    upload(formData: FormData) {
        return this.http.post<IUploadAttachmentResponse>(
            `${environment.baseUrl}/v1/attachment/uploadAttachment`,
            formData
        )
    }

    download(request: IDownloadAttachmentRequest) {
        return this.http.post<Blob>(
            `${environment.baseUrl}/v1/attachment/downloadAttachment`,
            request,
            { 
                responseType: 'blob' as 'json',
                observe: 'response'
             }
        )
    }

    downloadThumbnail(request: IDownloadAttachmentRequest) {
        return this.http.post<Blob>(
            `${environment.baseUrl}/v1/attachment/downloadAttachmentThumbnail`,
            request,
            { 
                responseType: 'blob' as 'json',
                observe: 'response'
             }
        )
    }

    downloadMidsize(request: IDownloadAttachmentRequest) {
        return this.http.post<Blob>(
            `${environment.baseUrl}/v1/attachment/downloadAttachmentMidsize`,
            request,
            { 
                responseType: 'blob' as 'json',
                observe: 'response'
             }
        )
    }

    getAttachmentsByAssignment(request: IGetAttachmentsByAssignmentResponse){
        return this.http.post<IPagedResults<IAttachmentResponse[]>>(
            `${environment.baseUrl}/v1/attachment/getAttachmentsByAssignment`,
            request
        )
    }

    getAttachmentsByContact(request: IGetAttachmentsByContactResponse){
        return this.http.post<IPagedResults<IAttachmentResponse[]>>(
            `${environment.baseUrl}/v1/attachment/getAttachmentsByContact`,
            request
        )
    }

    getAttachmentsByEmployee(request: IGetAttachmentsByEmployeeResponse){
        return this.http.post<IPagedResults<IAttachmentResponse[]>>(
            `${environment.baseUrl}/v1/attachment/getAttachmentsByEmployee`,
            request
        )
    }

    getAttachmentsByExpense(request: IGetAttachmentsByExpenseResponse){
        return this.http.post<IPagedResults<IAttachmentResponse[]>>(
            `${environment.baseUrl}/v1/attachment/getAttachmentsByExpense`,
            request
        )
    }

    delete(id :string) {
        return this.http.delete(
            `${environment.baseUrl}/v1/attachment/deleteAttachment/${id}`
        )
    }

    hasThumbnail(id: string) {
        return this.http.get(
            `${environment.baseUrl}/v1/attachment/checkAttachmentThumbnail/${id}`
        )
    }

    // Polling function: waits until the thumbnail is generated
    public async waitForThumbnail(attachmentId: string, retries = 2, delayMs = 500): Promise<boolean> {
        for (let i = 0; i < retries; i++) {
            const hasThumb = await firstValueFrom(this.hasThumbnail(attachmentId));
            if (hasThumb) return true;
            await new Promise(r => setTimeout(r, delayMs));
        }
        
        return false;
    }
}

export interface IAttachmentResponse {
    id: string,
    fileExtension: string,
    data?: Blob | string,
    mimeType?: string,
    fileName?: string
}

export interface IUploadAttachmentRequest {
    file: FormData
}

export interface IUploadAttachmentResponse {
    id: string,
    fileExtension: string,
    assignmentId?: string,
    contactId? : string,
    employeeId? : string,
    expenseId? : string,
}

export interface IDownloadAttachmentRequest {
    attachmentId: string;
}

export interface IDownloadAttachmentResponse {
    data: Blob;
    filename: string;
    mimeType: string;
}

export interface IGetAttachmentsByAssignmentResponse {
    assignmentId: string;
}

export interface IGetAttachmentsByContactResponse {
    contactId: string;
}

export interface IGetAttachmentsByEmployeeResponse {
    employeeId: string;
}

export interface IGetAttachmentsByExpenseResponse {
    expenseId: string;
}